import React from 'react';
import { ActivityIndicator, TouchableOpacity, View, StyleSheet } from 'react-native';
import { useRouter } from 'expo-router';
import { MaterialIcons } from '@expo/vector-icons';

import { useAuth } from '../../context/AuthContext';
import { useTrialContext } from '../../context/TrialContext';
import { useTranslation } from '../../hooks/useTranslation';
import { colors, spacing } from '../../design-system';
import { ROUTES } from '../../navigation/routes';
import TrialUploadCard from './TrialUploadCard';
import { authAPI, trialAPI, type OnboardingPath, type IntakeSubmissionPayload } from '../../services/api';
import { trackFeatureUsage } from '../../services/analytics';
import type { IntakeSummary } from '../../types';
import { getTrialUrgencyColors } from './TrialBanner';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Text } from '@/components/ui/text';

type TrialWelcomeBannerProps = {
  intakeSummary?: IntakeSummary | null;
  onIntakeUpdated?: (summary: IntakeSummary) => void;
};

type TrialPathOption = {
  path: OnboardingPath;
  icon: React.ComponentProps<typeof MaterialIcons>['name'];
  profileType: 'student' | 'coach' | 'mentor';
  goalEn: string;
  goalTr: string;
  titleFallback: string;
  descriptionFallback: string;
  headlineFallback: string;
  bodyFallback: string;
  uploadFallback: string;
};

const TRIAL_PATH_OPTIONS: TrialPathOption[] = [
  {
    path: 'session_analysis',
    icon: 'analytics',
    profileType: 'coach',
    goalEn: 'Analyze a real client session and improve my coaching presence',
    goalTr: 'Gerçek bir müşteri oturumunu analiz edip koçluk varlığımı geliştirmek',
    titleFallback: 'Analyze a client session',
    descriptionFallback: 'Use a recent transcript while the session is still fresh.',
    headlineFallback: 'Turn a real client session into concrete coaching feedback',
    bodyFallback: 'Your trial will prioritize transcript analysis, ICF competency signals, and next-session improvements.',
    uploadFallback: 'Upload the session you most want feedback on. Mentor Coaching AI will use your saved focus to shape the first analysis and follow-up chat.',
  },
  {
    path: 'credential_prep',
    icon: 'military-tech',
    profileType: 'coach',
    goalEn: 'Prepare for ACC/PCC/MCC credentialing with evidence-based feedback',
    goalTr: 'Kanıta dayalı geri bildirimle ACC/PCC/MCC akreditasyonuna hazırlanmak',
    titleFallback: 'Prepare for ACC/PCC/MCC',
    descriptionFallback: 'See where your coaching evidence is ready and where it needs work.',
    headlineFallback: 'Use the trial to see how credential-ready your coaching is',
    bodyFallback: 'Your trial will focus on ICF evidence, level signals, and the competency gaps that matter for credential preparation.',
    uploadFallback: 'Upload a session that represents your current coaching level. The app will keep your credential focus in later analysis and mentor chat.',
  },
  {
    path: 'mentor_review',
    icon: 'supervisor-account',
    profileType: 'mentor',
    goalEn: 'Review sessions faster as a mentor coach',
    goalTr: 'Mentor koç olarak oturumları daha hızlı incelemek',
    titleFallback: 'Review as a mentor coach',
    descriptionFallback: 'Find evidence, patterns, and supervision themes faster.',
    headlineFallback: 'Turn mentor-review work into a sharper evidence base',
    bodyFallback: 'Your trial will emphasize session evidence, development themes, and prompts you can use in mentor coaching conversations.',
    uploadFallback: 'Upload a session you need to review. Mentor Coaching AI will keep the mentor-review context available in later app guidance.',
  },
  {
    path: 'practice_first',
    icon: 'forum',
    profileType: 'student',
    goalEn: 'Practice with an AI client before uploading a real session',
    goalTr: 'Gerçek oturum yüklemeden önce yapay zeka müşteriyle pratik yapmak',
    titleFallback: 'Practice before uploading',
    descriptionFallback: 'Start with a simulated client, then analyze your real work.',
    headlineFallback: 'Start with practice, then bring a real session into the trial',
    bodyFallback: 'Your trial will make the first steps lighter: practice with an AI client, then use a real session when you are ready.',
    uploadFallback: 'You can upload now or start with the AI client. Your saved practice-first focus will carry into later guidance.',
  },
];

function getPathOption(path?: OnboardingPath | null): TrialPathOption {
  return TRIAL_PATH_OPTIONS.find((option) => option.path === path) || TRIAL_PATH_OPTIONS[0];
}

/**
 * Banner shown at the top of the dashboard for trial users.
 * Three states:
 * 1. Trial eligible (not started) → "Upload your first session"
 * 2. Trial active → progress indicator
 * 3. Trial expired → upgrade CTA
 */
export default function TrialWelcomeBanner({ intakeSummary, onIntakeUpdated }: TrialWelcomeBannerProps) {
  const router = useRouter();
  const { billingStatus, refreshAuth } = useAuth();
  const { trialStatus, refreshTrialStatus } = useTrialContext();
  const { t, language } = useTranslation();
  const [showUploader, setShowUploader] = React.useState(false);
  const storedPath = intakeSummary?.onboarding_path || null;
  const [selectedPath, setSelectedPath] = React.useState<OnboardingPath>(
    getPathOption(storedPath).path
  );
  const [savingPath, setSavingPath] = React.useState<OnboardingPath | null>(null);
  const [startingTrial, setStartingTrial] = React.useState(false);
  const [pathError, setPathError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setSelectedPath(getPathOption(storedPath).path);
  }, [storedPath]);

  const selectedOption = getPathOption(selectedPath);

  const buildPathPayload = React.useCallback((option: TrialPathOption): IntakeSubmissionPayload => {
    const existingGoal = intakeSummary?.primary_goal?.trim();
    return {
      profile_type: intakeSummary?.profile_type || option.profileType,
      onboarding_path: option.path,
      primary_goal: existingGoal || (language === 'tr' ? option.goalTr : option.goalEn),
      language,
      generate_suggestions: false,
    };
  }, [intakeSummary?.primary_goal, intakeSummary?.profile_type, language]);

  const savePath = React.useCallback(async (path: OnboardingPath) => {
    if (path === storedPath) {
      return true;
    }

    const option = getPathOption(path);
    setSavingPath(path);
    setPathError(null);

    try {
      const result = await authAPI.submitIntake(buildPathPayload(option));
      onIntakeUpdated?.(result);
      trackFeatureUsage('trial_onboarding_path', 'selected', {
        onboarding_path: path,
        profile_type: result.profile_type || option.profileType,
      });
      return true;
    } catch (err) {
      console.warn('Failed to save trial onboarding path', err);
      setPathError(t('trial.banner.pathSaveError'));
      return false;
    } finally {
      setSavingPath(null);
    }
  }, [buildPathPayload, onIntakeUpdated, storedPath, t]);

  const handleSelectPath = (path: OnboardingPath) => {
    setSelectedPath(path);
    void savePath(path);
  };

  const handleStartTrial = async () => {
    setStartingTrial(true);
    setPathError(null);

    const pathSavePromise =
      selectedPath === storedPath
        ? Promise.resolve(true)
        : authAPI.submitIntake(buildPathPayload(getPathOption(selectedPath)))
            .then((result) => {
              onIntakeUpdated?.(result);
              trackFeatureUsage('trial_onboarding_path', 'selected', {
                onboarding_path: selectedPath,
                profile_type: result.profile_type || getPathOption(selectedPath).profileType,
              });
              return true;
            })
            .catch((err) => {
              console.warn('Failed to save trial onboarding path before start', err);
              return false;
            });

    try {
      await trialAPI.start();
      void pathSavePromise.then((saved) => {
        if (!saved) {
          trackFeatureUsage('trial_onboarding_path', 'save_failed_non_blocking', {
            onboarding_path: selectedPath,
          });
        }
      });
      trackFeatureUsage('trial', 'start_clicked', { onboarding_path: selectedPath });
      await Promise.all([refreshTrialStatus(), refreshAuth()]);
      router.push(ROUTES.TABS_CHAT as any);
    } catch (err) {
      console.warn('Failed to start free trial', err);
      setPathError(t('trial.banner.startError'));
    } finally {
      setStartingTrial(false);
    }
  };

  if (!billingStatus) return null;

  const subscriptionActive = ['active'].includes(
    (billingStatus.subscription_status || '').toLowerCase()
  );

  // Don't show banner for active subscribers
  if (subscriptionActive) return null;

  const trialEligible = Boolean(billingStatus.free_trial_eligible);
  const trialActive = Boolean(billingStatus.free_trial_active);
  const trialExpired = Boolean(billingStatus.free_trial_expired);
  const allowLegacyNoCardTrialStart = false;

  if (trialEligible && !trialActive && !allowLegacyNoCardTrialStart) {
    // New acquisition now starts from Stripe Checkout with a card-backed
    // trial. Keep legacy active/expired trial states below, but do not offer a
    // new app-managed trial from inside the product.
    return null;
  }

  // State 1: Trial eligible, not started
  if (trialEligible && !trialActive) {
    if (showUploader) {
      return (
        <View style={styles.uploaderWrap}>
          <Card style={[styles.card, styles.uploaderHeaderCard]}>
            <CardContent style={styles.cardContent}>
              <View style={styles.iconRow}>
                <MaterialIcons name="bolt" size={26} color={colors.primary} />
                <View style={styles.textCol}>
                  <Text style={styles.kicker}>
                    {t('trial.banner.valueBadge')}
                  </Text>
                  <Text style={styles.title}>
                    {t(
                      `trial.banner.paths.${selectedOption.path}.uploadTitle`,
                      selectedOption.headlineFallback
                    )}
                  </Text>
                  <Text style={styles.description}>
                    {t(
                      `trial.banner.paths.${selectedOption.path}.uploadDescription`,
                      selectedOption.uploadFallback
                    )}
                  </Text>
                </View>
              </View>
            </CardContent>
          </Card>
          <TrialUploadCard
            language={language}
            onComplete={async (sessionId) => {
              await Promise.all([refreshTrialStatus(), refreshAuth()]);
              router.push({
                pathname: ROUTES.TABS_CHAT,
                params: { sessionId: String(sessionId) },
              } as any);
            }}
            onSkip={() => router.push(ROUTES.GATED_PLAN_SELECTION as any)}
          />
        </View>
      );
    }

    return (
      <Card style={styles.card}>
        <CardContent style={styles.cardContent}>
          <View style={styles.iconRow}>
            <MaterialIcons name="auto-awesome" size={28} color={colors.primary} />
            <View style={styles.textCol}>
              <Text style={styles.kicker}>
                {t('trial.banner.valueBadge')}
              </Text>
              <Text style={styles.title}>
                {t(
                  `trial.banner.paths.${selectedOption.path}.headline`,
                  selectedOption.headlineFallback
                )}
              </Text>
              <Text style={styles.description}>
                {t(
                  `trial.banner.paths.${selectedOption.path}.body`,
                  selectedOption.bodyFallback
                )}
              </Text>
            </View>
          </View>
          <View style={styles.pathSelector}>
            <Text style={styles.pathQuestion}>
              {t('trial.banner.pathQuestion')}
            </Text>
            <Text style={styles.pathDescription}>
              {t(
                'trial.banner.pathDescription'
              )}
            </Text>
            <View style={styles.pathGrid}>
              {TRIAL_PATH_OPTIONS.map((option) => {
                const selected = selectedPath === option.path;
                const saving = savingPath === option.path;
                return (
                  <TouchableOpacity
                    key={option.path}
                    onPress={() => handleSelectPath(option.path)}
                    disabled={!!savingPath}
                    accessibilityRole="button"
                    accessibilityState={{ selected, disabled: !!savingPath }}
                    style={[
                      styles.pathOption,
                      selected ? styles.pathOptionSelected : null,
                    ]}
                  >
                    <View style={styles.pathOptionHeader}>
                      <MaterialIcons
                        name={option.icon}
                        size={20}
                        color={selected ? colors.primary : colors.text.secondary}
                      />
                      {saving ? (
                        <ActivityIndicator size="small" color={colors.primary} />
                      ) : selected ? (
                        <MaterialIcons name="check-circle" size={18} color={colors.primary} />
                      ) : null}
                    </View>
                    <Text style={[styles.pathOptionTitle, selected ? styles.pathOptionTitleSelected : null]}>
                      {t(`trial.banner.paths.${option.path}.title`, option.titleFallback)}
                    </Text>
                    <Text style={styles.pathOptionDescription}>
                      {t(`trial.banner.paths.${option.path}.description`, option.descriptionFallback)}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
            {pathError ? <Text style={styles.errorText}>{pathError}</Text> : null}
          </View>
          <View style={styles.valueStack}>
            <View style={styles.valueRow}>
              <MaterialIcons name="schedule" size={18} color={colors.secondary} />
              <Text style={styles.valueText}>
                {t(
                  'trial.banner.urgencyDescription'
                )}
              </Text>
            </View>
            <View style={styles.valueRow}>
              <MaterialIcons name="verified-user" size={18} color={colors.success} />
              <Text style={styles.valueText}>
                {t('trial.banner.trialTerms')}
              </Text>
            </View>
          </View>
          <Button
            variant="default"
            size="sm"
            onPress={handleStartTrial}
            disabled={startingTrial}
            style={styles.ctaButton}
          >
            <Text style={styles.ctaText}>
              {startingTrial
                ? t('trial.banner.startingTrial')
                : t('trial.banner.startTrial')}
            </Text>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onPress={() => setShowUploader(true)}
            style={styles.secondaryButton}
          >
            <Text style={styles.secondaryText}>
              {t('trial.banner.uploadFirstCta')}
            </Text>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onPress={() => router.push(ROUTES.ACTIVATION_PRACTICE_SESSION as any)}
            style={styles.secondaryButton}
          >
            <Text style={styles.secondaryText}>
              {t('trial.banner.practiceCta')}
            </Text>
          </Button>
        </CardContent>
      </Card>
    );
  }

  // State 2: Trial active
  if (trialActive && trialStatus) {
    const chatCount = trialStatus.chat_count ?? 0;
    const remaining = trialStatus.messages_remaining ?? 0;
    const hoursLeft = trialStatus.hours_remaining ?? 0;
    const totalChats = Math.max(chatCount + remaining, chatCount, 1);
    const progressPercent = Math.min(100, (chatCount / totalChats) * 100);
    const urgencyColors = getTrialUrgencyColors(hoursLeft);

    return (
      <Card
        style={[
          styles.card,
          styles.activeCard,
          { borderColor: urgencyColors.border, backgroundColor: urgencyColors.bg[0] },
        ]}
      >
        <CardContent style={styles.cardContent}>
          <View style={styles.activeHeader}>
            <View style={styles.activeLabelRow}>
              <MaterialIcons name="science" size={22} color={urgencyColors.icon} />
              <Text style={[styles.kicker, styles.activeKicker, { color: urgencyColors.text }]}>
                {trialStatus.grandfathered
                  ? t('trial.banner.legacyLabel')
                  : trialStatus.kind === 'card_backed'
                    ? t('trial.banner.cardBackedLabel')
                    : t('trial.banner.label')}
              </Text>
            </View>
            <View style={[styles.timeChip, { backgroundColor: urgencyColors.badge }]}>
              <MaterialIcons name="schedule" size={14} color={urgencyColors.icon} />
              <Text style={[styles.timeChipText, { color: urgencyColors.text }]}>
                {t('trial.banner.hoursLeft', { count: Math.max(0, Math.round(hoursLeft)) })}
              </Text>
            </View>
          </View>
          <View style={styles.activeProgressHeader}>
            <Text style={[styles.activeStatus, { color: urgencyColors.text }]}>
              {t('trial.banner.messagesOf', { used: chatCount, total: totalChats })}
            </Text>
            {remaining === 0 ? (
              <Text style={[styles.activeStatus, { color: urgencyColors.text }]}>
                {t('trial.banner.noMessages')}
              </Text>
            ) : null}
          </View>
          <View style={styles.progressTrack}>
            <View
              style={[
                styles.progressFill,
                { width: `${progressPercent}%`, backgroundColor: urgencyColors.icon },
              ]}
            />
          </View>
          <Text style={styles.disclosureText}>
            {trialStatus.grandfathered
              ? t('trial.banner.legacyNotice')
              : t('trial.banner.cardBackedNotice')}
          </Text>
          <Button
            variant="default"
            size="sm"
            onPress={() => router.push(ROUTES.GATED_PLAN_SELECTION as any)}
            style={styles.ctaButton}
          >
            <Text style={styles.ctaText}>
              {t('trial.banner.subscribeCTA')}
            </Text>
          </Button>
        </CardContent>
      </Card>
    );
  }

  // State 3: Trial expired, no subscription
  if (trialExpired) {
    return (
      <Card style={[styles.card, styles.expiredCard]}>
        <CardContent style={styles.cardContent}>
          <View style={styles.iconRow}>
            <MaterialIcons name="timer-off" size={24} color="#DC2626" />
            <View style={styles.textCol}>
              <Text style={styles.title}>{t('trial.banner.expiredTitle')}</Text>
              <Text style={styles.description}>
                {t('trial.banner.expiredDescription')}
              </Text>
            </View>
          </View>
          <Button
            variant="default"
            size="sm"
            onPress={() => router.push(ROUTES.GATED_PLAN_SELECTION as any)}
            style={styles.ctaButton}
          >
            <Text style={styles.ctaText}>{t('trial.banner.viewPlans')}</Text>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return null;
}

const styles = StyleSheet.create({
  card: {
    marginHorizontal: spacing.md,
    marginTop: spacing.sm,
    borderWidth: 1,
    borderColor: `${colors.primary}30`,
  },
  uploaderWrap: {
    marginHorizontal: spacing.md,
    marginTop: spacing.sm,
  },
  uploaderHeaderCard: {
    marginHorizontal: 0,
    marginTop: 0,
  },
  activeCard: {
    overflow: 'hidden',
  },
  activeHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  activeLabelRow: {
    minWidth: 0,
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  activeKicker: {
    marginBottom: 0,
  },
  timeChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 999,
  },
  timeChipText: {
    fontSize: 12,
    fontWeight: '700',
    fontVariant: ['tabular-nums'],
  },
  activeProgressHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  activeStatus: {
    fontSize: 13,
    fontWeight: '600',
    fontVariant: ['tabular-nums'],
  },
  disclosureText: {
    color: colors.text.secondary,
    fontSize: 12,
    lineHeight: 17,
    marginTop: spacing.sm,
  },
  expiredCard: {
    borderColor: '#DC2626' + '30',
  },
  cardContent: {
    padding: spacing.md,
  },
  iconRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  textCol: {
    flex: 1,
  },
  kicker: {
    fontSize: 12,
    fontWeight: '700',
    color: colors.primary,
    textTransform: 'uppercase',
    marginBottom: 3,
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text.primary,
    marginBottom: 2,
  },
  description: {
    fontSize: 14,
    color: colors.text.secondary,
    lineHeight: 20,
  },
  ctaButton: {
    alignSelf: 'flex-start',
    marginTop: spacing.xs,
  },
  secondaryButton: {
    alignSelf: 'flex-start',
    marginTop: spacing.sm,
  },
  ctaText: {
    color: '#FFF',
    fontSize: 14,
    fontWeight: '600',
  },
  secondaryText: {
    color: colors.primary,
    fontSize: 14,
    fontWeight: '600',
  },
  valueStack: {
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  pathSelector: {
    marginTop: spacing.xs,
    marginBottom: spacing.md,
  },
  pathQuestion: {
    fontSize: 14,
    fontWeight: '700',
    color: colors.text.primary,
    marginBottom: 3,
  },
  pathDescription: {
    fontSize: 13,
    color: colors.text.secondary,
    lineHeight: 18,
    marginBottom: spacing.sm,
  },
  pathGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  pathOption: {
    width: '48%',
    minWidth: 150,
    flexGrow: 1,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    padding: spacing.sm,
    backgroundColor: '#FFFFFF',
  },
  pathOptionSelected: {
    borderColor: colors.primary,
    backgroundColor: `${colors.primary}10`,
  },
  pathOptionHeader: {
    minHeight: 22,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.xs,
  },
  pathOptionTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: colors.text.primary,
    marginBottom: 2,
  },
  pathOptionTitleSelected: {
    color: colors.primary,
  },
  pathOptionDescription: {
    fontSize: 12,
    color: colors.text.secondary,
    lineHeight: 16,
  },
  errorText: {
    marginTop: spacing.sm,
    fontSize: 12,
    color: '#DC2626',
  },
  valueRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
  },
  valueText: {
    flex: 1,
    fontSize: 13,
    color: colors.text.secondary,
    lineHeight: 18,
  },
  progressTrack: {
    height: 6,
    borderRadius: 3,
    backgroundColor: '#E5E7EB',
    marginTop: spacing.xs,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 3,
    backgroundColor: '#059669',
  },
});
